import { omit } from "lodash-es";
import { createReducer } from "typesafe-actions";

import ChatFailureType from "@mapmycustomers/shared/enum/chat/ChatFailureType";
import ChatMessageRole from "@mapmycustomers/shared/enum/chat/ChatMessageRole";
import ChatProposalApplyOutcome from "@mapmycustomers/shared/enum/chat/ChatProposalApplyOutcome";
import ChatUserMessageStatus from "@mapmycustomers/shared/enum/chat/ChatUserMessageStatus";
import ChatMessage, {
  AssistantChatMessage,
  UserChatMessage,
} from "@mapmycustomers/shared/types/chat/ChatMessage";
import { ParsedFieldAgentApplyResultItem } from "@mapmycustomers/shared/types/chat/FieldAgentChat";

import { ChatThreadKey } from "@app/util/chat/getChatThreadKey";
import {
  ChatProposalOutcomeDetail,
  ChatProposalStatusOverride,
  ChatProposalUiState,
} from "@app/util/chat/proposalRowStatus";

import {
  appendLocalChatMessage,
  applyChatMessageFeedback,
  applyProposals,
  cancelProposals,
  ChatConversationActions,
  clearChatConversation,
  clearChatThreadByKey,
  discardProposalDraft,
  queueChatMessage,
  restoreChatConversation,
  retryChatMessage,
  saveProposalDraft,
  sendChatMessage,
  setChatMessageFeedback,
  toggleProposalChecked,
} from "./actions";
import type ChatConversationState from "./ChatConversationState";
import { type ChatThreadState, emptyChatThread } from "./ChatConversationState";

export const chatConversationInitialState: ChatConversationState = {
  byThread: {},
  proposalApply: {},
  proposalUi: {},
};

const updateThread = (
  state: ChatConversationState,
  threadKey: ChatThreadKey,
  update: (thread: ChatThreadState) => ChatThreadState
): ChatConversationState => ({
  ...state,
  byThread: {
    ...state.byThread,
    [threadKey]: update(state.byThread[threadKey] ?? emptyChatThread),
  },
});

// Retry and feedback dispatch a message id alone, so the thread holding it has to be found. Ids
// are unique across threads, so at most one matches.
const updateMessagesById = (
  state: ChatConversationState,
  id: ChatMessage["id"],
  update: (messages: readonly ChatMessage[]) => ChatMessage[]
): ChatConversationState => {
  const threadKey = (Object.keys(state.byThread) as ChatThreadKey[]).find((candidate) =>
    state.byThread[candidate]?.messages.some((message) => message.id === id)
  );
  return threadKey === undefined
    ? state
    : updateThread(state, threadKey, (thread) => ({
        ...thread,
        messages: update(thread.messages),
      }));
};

const updateUserMessage = (
  messages: readonly ChatMessage[],
  id: ChatMessage["id"],
  update: (message: UserChatMessage) => UserChatMessage
): ChatMessage[] =>
  messages.map((message) =>
    message.id === id && message.role === ChatMessageRole.USER ? update(message) : message
  );

const hasUserMessage = (messages: readonly ChatMessage[], id: ChatMessage["id"]): boolean =>
  messages.some((message) => message.id === id && message.role === ChatMessageRole.USER);

const updateAssistantMessage = (
  messages: readonly ChatMessage[],
  id: ChatMessage["id"],
  update: (message: AssistantChatMessage) => AssistantChatMessage
): ChatMessage[] =>
  messages.map((message) =>
    message.id === id && message.role === ChatMessageRole.ASSISTANT ? update(message) : message
  );

// The outcomes that settle a row for good. Membership only: the reducer stores the outcome itself,
// and what it looks like on screen is proposalRowStatus.ts's business.
const TERMINAL_OUTCOMES: ChatProposalStatusOverride[] = [
  ChatProposalApplyOutcome.APPLIED,
  ChatProposalApplyOutcome.CANCELLED,
  ChatProposalApplyOutcome.EXPIRED,
  ChatProposalApplyOutcome.FAILED,
  ChatProposalApplyOutcome.STALE,
];

// Shared by the CONFLICT and INVALID branches below: both attach the same three wire fields, and
// differ only in what else survives alongside them (statusOverride vs. spread previous).
const buildOutcomeDetail = (
  result: ParsedFieldAgentApplyResultItem
): ChatProposalOutcomeDetail => ({
  failureReason: result.failureReason,
  fieldErrors: result.fieldErrors,
  freshValues: result.freshValues,
});

// What the apply did, alone — the record it created, or the stops an add wrote and left off: a
// failure detail riding along would surface under an applied row's green check. The stops arrive
// already parsed: the apply saga checks them, where a dropped entry can be logged.
const buildAppliedDetail = ({
  createdEntityId,
  createdEntityName,
  createdEntityType,
  routeStops,
}: ParsedFieldAgentApplyResultItem): ChatProposalOutcomeDetail | undefined => {
  const detail: ChatProposalOutcomeDetail = {
    ...(createdEntityId === undefined ? {} : { createdEntityId, createdEntityType }),
    ...(createdEntityName ? { createdEntityName } : {}),
    ...(routeStops ? { routeStops } : {}),
  };
  return Object.keys(detail).length ? detail : undefined;
};

// Folds one apply result into the proposal-ui map. Terminal outcomes settle the row, keeping its
// draft and — on `applied` alone — what it did (check state and any stale error stop
// mattering once settled);
// conflict settles the row too, but keeps its draft and freshValues so it can still render a
// before → after;
// invalid keeps the row pending and attaches the failure detail; not_found hides the row. The final
// fall-through is unreachable through the type, and kept for the deploy where BE ships a new outcome
// before we ship support for it: the row stays exactly as it was.
const foldOutcome = (
  proposalUi: Record<number, ChatProposalUiState>,
  result: ParsedFieldAgentApplyResultItem
): Record<number, ChatProposalUiState> => {
  const previous = proposalUi[result.proposalId];
  const terminal = TERMINAL_OUTCOMES.find((candidate) => candidate === result.outcome);
  if (terminal) {
    // The draft is the only record of what the rep applied — the result echoes no values back. The
    // created record is kept on `applied` alone, so a rejected row can never hold a link target.
    return {
      ...proposalUi,
      [result.proposalId]: {
        draft: previous?.draft,
        outcome:
          terminal === ChatProposalApplyOutcome.APPLIED ? buildAppliedDetail(result) : undefined,
        statusOverride: terminal,
      },
    };
  }
  if (result.outcome === ChatProposalApplyOutcome.NOT_FOUND) {
    return { ...proposalUi, [result.proposalId]: { hidden: true } };
  }
  // Conflict settles the row — the proposal can never apply, because BE compares the Deal's live
  // stage against the frozen baseline in the stored proposal and nothing rewrites it. But unlike a
  // terminal outcome the row still renders: freshValues carries the new "before", the draft the
  // rep's "after". So it settles without being forgotten.
  if (result.outcome === ChatProposalApplyOutcome.CONFLICT) {
    return {
      ...proposalUi,
      [result.proposalId]: {
        draft: previous?.draft,
        outcome: buildOutcomeDetail(result),
        statusOverride: ChatProposalApplyOutcome.CONFLICT,
      },
    };
  }
  if (result.outcome === ChatProposalApplyOutcome.INVALID) {
    return {
      ...proposalUi,
      [result.proposalId]: { ...previous, outcome: buildOutcomeDetail(result) },
    };
  }
  return proposalUi;
};

const chatConversation = createReducer<ChatConversationState, ChatConversationActions>(
  chatConversationInitialState
)
  .handleAction(clearChatConversation, () => chatConversationInitialState)
  // Dropped rather than emptied: emptyChatThread reads the same for both, and keeping the key
  // would spend a retention slot on nothing.
  .handleAction(clearChatThreadByKey, (state, { payload }) => ({
    ...state,
    byThread: omit(state.byThread, payload),
  }))
  .handleAction(queueChatMessage, (state, { payload }) =>
    updateThread(state, payload.threadKey, (thread) => ({
      ...thread,
      messages: [
        ...thread.messages,
        {
          id: payload.id,
          context: payload.context,
          contextDetached: payload.contextDetached,
          contextPinned: payload.contextPinned,
          createdAt: payload.createdAt,
          role: ChatMessageRole.USER,
          status: ChatUserMessageStatus.SENDING,
          text: payload.text,
          voice: payload.voice,
        },
      ],
      // The chips belonged to the turn that just ended.
      suggestions: [],
    }))
  )
  .handleAction(restoreChatConversation, (state, { payload }) => ({
    ...state,
    byThread: Object.fromEntries(
      Object.entries(payload.byThread).map(([threadKey, thread]) => [
        threadKey,
        { ...thread, suggestions: [] },
      ])
    ),
    // Never persisted: a reload always lands back on actionable, in-flight or not.
    proposalApply: {},
    proposalUi: payload.proposalUi ?? {},
  }))
  .handleAction(retryChatMessage, (state, { payload }) =>
    updateMessagesById(state, payload.id, (messages) =>
      updateUserMessage(messages, payload.id, (message) => ({
        ...message,
        failureType: undefined,
        status: ChatUserMessageStatus.SENDING,
      }))
    )
  )
  .handleAction(sendChatMessage.success, (state, { payload }) => {
    const target = state.byThread[payload.threadKey];
    // A reply whose originating message is gone arrived after clearChatConversation wiped the
    // thread (sign-out/user-switch with the request in flight). Appending it would splice the
    // previous user's reply — and resurrect their conversationId — into the new thread, which
    // the persist watcher would then store under the new user's id. Drop it instead. A reply for
    // a thread that never existed is dropped the same way.
    if (!target || !hasUserMessage(target.messages, payload.id)) {
      return state;
    }
    return updateThread(state, payload.threadKey, (thread) => ({
      ...thread,
      conversationId: payload.conversationId,
      messages: [
        ...updateUserMessage(thread.messages, payload.id, (message) => {
          // Only failed messages are retried, so a sent one stops carrying (and persisting) its context.
          const { context, ...rest } = message;
          return { ...rest, status: ChatUserMessageStatus.SENT };
        }),
        // Always at the end, even when the message being answered is not the last one — so a
        // retried message's reply reads as an answer to whatever the rep asked after it.
        // Accepted while TEXT is the only content type: inserting mid-thread would mean the
        // persisted order no longer matches the order the rep saw, for a case (retrying an old
        // message rather than the newest one) that is rare and already flagged by its own icon.
        ...payload.messages,
      ],
      suggestions: payload.suggestions,
    }));
  })
  .handleAction(sendChatMessage.failure, (state, { payload }) => {
    const target = state.byThread[payload.threadKey];
    // The failing turn belongs to a thread clearChatConversation already wiped (sign-out/
    // user-switch with the request in flight). Dropping the conversation id now would discard
    // the one the new conversation has since earned, restarting it server-side.
    if (!target || !hasUserMessage(target.messages, payload.id)) {
      return state;
    }
    return updateThread(state, payload.threadKey, (thread) => ({
      ...thread,
      // A 404 means the server has no such conversation, so the next attempt must start a new
      // one rather than quoting an id that will fail again.
      conversationId:
        payload.failureType === ChatFailureType.CONVERSATION_GONE
          ? undefined
          : thread.conversationId,
      messages: updateUserMessage(thread.messages, payload.id, (message) => ({
        ...message,
        failureType: payload.failureType,
        status: ChatUserMessageStatus.FAILED,
      })),
    }));
  })
  // Not setChatMessageFeedback.request: see the comment on that action in actions.ts for why
  // the optimistic write has to be its own action rather than living on the request.
  .handleAction([applyChatMessageFeedback, setChatMessageFeedback.failure], (state, { payload }) =>
    updateMessagesById(state, payload.id, (messages) =>
      updateAssistantMessage(messages, payload.id, (message) => ({
        ...message,
        feedback: payload.feedback,
      }))
    )
  )
  .handleAction(appendLocalChatMessage, (state, { payload: { threadKey, ...message } }) =>
    updateThread(state, threadKey, (thread) => ({
      ...thread,
      messages: [...thread.messages, { ...message, role: ChatMessageRole.LOCAL }],
    }))
  )
  .handleAction(toggleProposalChecked, (state, { payload }) => ({
    ...state,
    proposalUi: {
      ...state.proposalUi,
      [payload.proposalId]: { ...state.proposalUi[payload.proposalId], checked: payload.checked },
    },
  }))
  .handleAction(saveProposalDraft, (state, { payload }) => ({
    ...state,
    proposalUi: {
      ...state.proposalUi,
      // A re-edit clears the stale error: the rep is trying again, so the old outcome no
      // longer describes what's about to be submitted.
      [payload.proposalId]: {
        ...state.proposalUi[payload.proposalId],
        draft: payload.draft,
        outcome: undefined,
      },
    },
  }))
  .handleAction(discardProposalDraft, (state, { payload }) => ({
    ...state,
    proposalUi: {
      ...state.proposalUi,
      [payload.proposalId]: { ...state.proposalUi[payload.proposalId], draft: undefined },
    },
  }))
  // Both gestures share the card's single busy slot. The cancel saga must NOT dispatch
  // applyProposals.request—sagas.ts takes every one and would run the Confirm handler too.
  .handleAction([applyProposals.request, cancelProposals], (state, { payload }) => ({
    ...state,
    proposalApply: { ...state.proposalApply, [payload.messageId]: "inFlight" },
  }))
  .handleAction(applyProposals.failure, (state, { payload }) => ({
    ...state,
    proposalApply: { ...state.proposalApply, [payload.messageId]: "failed" },
  }))
  .handleAction(applyProposals.success, (state, { payload }) => {
    const proposalApply = { ...state.proposalApply };
    delete proposalApply[payload.messageId];
    return {
      ...state,
      proposalApply,
      proposalUi: payload.results.reduce(foldOutcome, state.proposalUi),
    };
  });

export default chatConversation;
